import React, { useEffect, useState } from "react";
import { toast, Toaster } from "sonner";
import {
  Mail, Users, TrendingUp, ShieldOff, PlayCircle, Loader2, RefreshCw, Send,
} from "lucide-react";
import DashboardShell from "../../components/layout/DashboardShell";
import { Card, Pill } from "../../components/common/Primitives";
import { Button } from "../../components/ui/button";
import api, { formatApiError } from "../../lib/api";

/**
 * NurtureMetrics
 * --------------
 * Admin ops panel. Shows how the sector-report lead nurture sequence is
 * performing so the copy can be tuned:
 *   • Total leads
 *   • Touch delivery counts (T1 / T2 / T3)
 *   • Opt-out rate
 *   • Marketplace-conversion rate + per-touch breakdown
 *   • Leads by month
 */
function StatTile({ label, value, sub, testId }) {
  return (
    <div className="p-5 rounded-md border border-navy/10 bg-white" data-testid={testId}>
      <div className="text-[10px] font-mono uppercase tracking-[0.22em] text-ink-muted">{label}</div>
      <div className="font-heading text-ink text-2xl mt-2">{value}</div>
      {sub ? <div className="text-[11px] font-mono text-ink-muted mt-1">{sub}</div> : null}
    </div>
  );
}

function Bar({ pct, testId }) {
  return (
    <div className="h-1.5 bg-navy/10 rounded-full overflow-hidden" data-testid={testId}>
      <div className="h-full bg-navy" style={{ width: `${Math.min(100, Math.max(0, pct))}%` }} />
    </div>
  );
}

export default function NurtureMetrics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/sector-reports/nurture-metrics");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/sector-reports/nurture-now");
      toast.success(`Nurture ran — ${r.data.total || 0} email(s) dispatched.`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRunning(false);
    }
  };

  if (loading) {
    return (
      <DashboardShell title="Nurture Metrics" subtitle="ADMIN · SECTOR REPORT">
        <Card><div className="flex items-center gap-2 text-ink-muted text-sm"><Loader2 size={14} className="animate-spin" /> Loading metrics…</div></Card>
      </DashboardShell>
    );
  }
  if (!data) return null;

  const t = data.totals || {};
  const r = data.rates || {};
  const touches = data.touches || {};
  const conv = data.conversions_by_touch || {};

  return (
    <DashboardShell title="Nurture Metrics" subtitle="ADMIN · SECTOR REPORT">
      <Toaster richColors position="top-right" />

      {/* header actions */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="text-sm text-ink-soft max-w-xl">
          How the 3-touch onboarding sequence is landing across sector-report leads. Use the &quot;Send touches now&quot; button to force a scheduler pass for testing.
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} data-testid="nurture-refresh-btn">
            <RefreshCw size={14} className="mr-1.5" /> Refresh
          </Button>
          <Button
            size="sm"
            onClick={runNow}
            disabled={running}
            className="bg-navy hover:bg-[#256E58] text-white"
            data-testid="nurture-run-now-btn"
          >
            {running ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />}
            {running ? "Sending…" : "Send touches now"}
          </Button>
        </div>
      </div>

      {/* top tiles */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="nurture-top-tiles">
        <StatTile label="Total leads" value={t.leads ?? 0} sub={`Since launch`} testId="tile-total-leads" />
        <StatTile
          label="Directory conversion"
          value={`${r.conversion_pct ?? 0}%`}
          sub={`${t.visited_marketplace ?? 0} of ${t.leads ?? 0} visited`}
          testId="tile-conversion"
        />
        <StatTile
          label="Opt-out rate"
          value={`${r.opt_out_pct ?? 0}%`}
          sub={`${t.opted_out ?? 0} chose to opt out`}
          testId="tile-opt-out"
        />
        <StatTile
          label="In sequence"
          value={t.in_sequence ?? 0}
          sub="Leads waiting for next touch"
          testId="tile-in-sequence"
        />
      </div>

      {/* touch delivery */}
      <Card testId="nurture-delivery-card">
        <div className="flex items-start gap-3 mb-5">
          <Mail size={17} className="text-[#8A6600] mt-0.5" />
          <div>
            <div className="font-heading text-ink text-lg">Touch delivery</div>
            <div className="text-xs text-ink-muted">Leads that have received each touch. Delivery is cumulative — T3 leads have also received T1 and T2.</div>
          </div>
        </div>
        <div className="space-y-4">
          {[
            { key: "t1", label: "T1 · What you missed (day 2)", value: touches.t1_sent, pct: r.t1_delivery_pct },
            { key: "t2", label: "T2 · Peers are watching (day 5)", value: touches.t2_sent, pct: r.t2_delivery_pct },
            { key: "t3", label: "T3 · One last note (day 10)", value: touches.t3_sent, pct: r.t3_delivery_pct },
          ].map((row) => (
            <div key={row.key} data-testid={`nurture-delivery-${row.key}`}>
              <div className="flex justify-between text-sm mb-1.5">
                <span className="text-ink font-heading">{row.label}</span>
                <span className="font-mono text-ink-muted">{row.value ?? 0} <span className="text-ink-faint">· {row.pct ?? 0}%</span></span>
              </div>
              <Bar pct={row.pct ?? 0} testId={`nurture-bar-${row.key}`} />
            </div>
          ))}
        </div>
      </Card>

      {/* conversions by touch */}
      <div className="mt-6">
        <Card testId="nurture-conversion-card">
          <div className="flex items-start gap-3 mb-5">
            <TrendingUp size={17} className="text-[#8A6600] mt-0.5" />
            <div>
              <div className="font-heading text-ink text-lg">Directory conversion by touch</div>
              <div className="text-xs text-ink-muted">Where the visit happened — helps tune copy strength per touch.</div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { key: "no_touch", label: "Before any touch", value: conv.no_touch },
              { key: "after_t1", label: "After T1 only", value: conv.after_t1_only },
              { key: "after_t2", label: "After T2 only", value: conv.after_t2_only },
              { key: "after_t3", label: "After T3", value: conv.after_t3 },
            ].map((c) => (
              <div key={c.key} className="p-4 rounded-md border border-navy/10 bg-cream/60" data-testid={`nurture-conv-${c.key}`}>
                <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-ink-muted">{c.label}</div>
                <div className="font-heading text-ink text-xl mt-1.5">{c.value ?? 0}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* by month */}
      <div className="mt-6">
        <Card testId="nurture-by-month-card">
          <div className="flex items-start gap-3 mb-5">
            <Users size={17} className="text-[#8A6600] mt-0.5" />
            <div>
              <div className="font-heading text-ink text-lg">Leads by month</div>
              <div className="text-xs text-ink-muted">Last 12 months of sector-report requests.</div>
            </div>
          </div>
          {(data.by_month || []).length === 0 ? (
            <div className="text-sm text-ink-muted">No leads yet.</div>
          ) : (
            <div className="divide-y divide-navy/[0.08]">
              {data.by_month.map((row) => {
                const label = new Date(`${row.month}-01`).toLocaleDateString(undefined, { month: "long", year: "numeric" });
                return (
                  <div key={row.month} className="flex items-center justify-between py-2.5" data-testid={`nurture-month-${row.month}`}>
                    <span className="text-sm text-ink">{label}</span>
                    <Pill tone="muted">{row.leads} lead{row.leads === 1 ? "" : "s"}</Pill>
                  </div>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* footer note */}
      <div className="mt-8 flex items-center gap-2 text-[11px] font-mono uppercase tracking-widest text-ink-muted">
        <ShieldOff size={12} />
        <span>Opted-out leads are excluded from all touches. Directory visits stop future touches automatically.</span>
        <PlayCircle size={12} className="ml-auto" />
      </div>
    </DashboardShell>
  );
}
